/**
 * HICC PDP — Subscribe & Save UI synced with Seal Subscriptions (Merchant JS API).
 * Plan data from Shopify variant.selling_plan_allocations (configured in Seal app).
 */
(function () {
  function formatMoney(cents, format) {
    if (cents == null || isNaN(cents)) return '—';
    const amount = (Number(cents) / 100).toFixed(2);
    if (!format) return '$' + amount;
    if (format.includes('amount')) {
      return format.replace(/\{\{\s*amount\s*\}\}/, amount).replace(/&gt;/g, '>').replace(/&lt;/g, '<');
    }
    return '$' + amount;
  }

  function parseCents(value) {
    if (value == null) return null;
    if (typeof value === 'number') return value;
    if (typeof value === 'object' && value !== null) {
      if ('amount' in value) return Number(value.amount);
      if ('value' in value) return Number(value.value);
    }
    return null;
  }

  function pctOff(compareCents, priceCents) {
    if (!compareCents || !priceCents || compareCents <= 0) return 0;
    return Math.round((1 - priceCents / compareCents) * 100);
  }

  function formatPlanLabel(plan) {
    if (!plan) return 'Subscription';
    const options = plan.options || [];
    const values = options.map((o) => o.value).filter(Boolean);
    if (values.length) return values.join(' · ');
    const name = (plan.name || '').trim();
    if (name && !/^subscription$/i.test(name)) return name;
    return name || 'Subscription';
  }

  function planIdFromAllocation(allocation) {
    if (!allocation) return '';
    return String(allocation.selling_plan_id || (allocation.selling_plan && allocation.selling_plan.id) || '');
  }

  function setPriceSlot(valueEl, formatted) {
    if (!valueEl) return;
    const slot = valueEl.closest('[data-pdp-price-slot]');
    const skeleton = slot && slot.querySelector('[data-pdp-price-skeleton]');
    if (!formatted) {
      valueEl.textContent = '';
      valueEl.classList.add('invisible');
      if (skeleton) skeleton.classList.remove('hidden');
      return;
    }
    valueEl.textContent = formatted;
    valueEl.classList.remove('invisible');
    if (skeleton) skeleton.classList.add('hidden');
  }

  function setPctBadge(pctEl, text) {
    if (!pctEl) return;
    const skeleton = pctEl.parentElement && pctEl.parentElement.querySelector('[data-pdp-future-pct-skeleton]');
    if (!text) {
      pctEl.textContent = '';
      pctEl.classList.add('hidden');
      if (skeleton) skeleton.classList.add('hidden');
      return;
    }
    pctEl.textContent = text;
    pctEl.classList.remove('hidden');
    if (skeleton) skeleton.classList.add('hidden');
  }

  function setFuturePctSkeleton(pctEl, loading) {
    if (!pctEl) return;
    const skeleton = pctEl.parentElement && pctEl.parentElement.querySelector('[data-pdp-future-pct-skeleton]');
    if (!skeleton) return;
    if (loading) {
      pctEl.textContent = '';
      pctEl.classList.add('hidden');
      skeleton.classList.remove('hidden');
    } else {
      skeleton.classList.add('hidden');
    }
  }

  function setSaveSlot(root, html) {
    const saveEl = root.querySelector('[data-pdp-sub-save-text]');
    const saveSlot = root.querySelector('[data-pdp-save-slot]');
    const skeleton = saveSlot && saveSlot.querySelector('[data-pdp-save-skeleton]');
    if (!saveEl) return;
    if (!html) {
      saveEl.innerHTML = '';
      saveEl.classList.add('invisible');
      if (skeleton) skeleton.classList.remove('hidden');
      return;
    }
    saveEl.innerHTML = html;
    saveEl.classList.remove('invisible');
    if (skeleton) skeleton.classList.add('hidden');
  }

  const PDP_MOBILE_MAX_WIDTH = '(max-width: 1023px)';

  function resolveDefaultPurchaseMode(config) {
    if (!config || config.defaultMode === false) return 'onetime';
    if (config.defaultOnetimeOnMobile !== false && window.matchMedia(PDP_MOBILE_MAX_WIDTH).matches) {
      return 'onetime';
    }
    return 'subscribe';
  }

  function paymentTermsReady(el) {
    if (!el) return false;
    const wc = el.querySelector('shopify-payment-terms');
    if (wc && wc.shadowRoot) {
      const shadowText = (wc.shadowRoot.textContent || '').replace(/\s+/g, ' ').trim();
      if (shadowText.length > 8) return true;
    }
    const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    return text.length > 8;
  }

  class PdpSubscriptionHicc {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.formId = root.dataset.productFormId;
      this.config = (window.PdpSubscriptionHiccConfig && window.PdpSubscriptionHiccConfig[this.sectionId]) || {};
      this.moneyFormat = this.config.moneyFormat;
      this.mode = resolveDefaultPurchaseMode(this.config);
      this.variants = this.readVariants();
      this.plansByVariant = this.readPlansByVariant();
      this.variantId = String(root.dataset.variantId);
      this.sealMount = root.querySelector('[data-seal-mount]');
      this.bindUi();
      this.bindAddToCart();
      this.bindSealEvents();
      this.bindVariantEvents();
      this.applyVariant(this.variantId);
      this.setMode(this.mode);
      if (this.isQuickViewContext()) this.syncHeaderPriceBlockVisibility(this.root.dataset.supported === 'true');
    }

    readVariants() {
      const el = document.getElementById('pdp-subscription-variants-' + this.sectionId);
      if (!el) return [];
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return [];
      }
    }

    readPlansByVariant() {
      const el = document.getElementById('pdp-subscription-plans-' + this.sectionId);
      if (!el) return {};
      try {
        return JSON.parse(el.textContent) || {};
      } catch (e) {
        return {};
      }
    }

    getPlanMeta(variantId, planId) {
      const list = this.plansByVariant[String(variantId)] || [];
      return list.find((p) => String(p.selling_plan_id) === String(planId)) || null;
    }

    isIntroOffer(meta, allocation) {
      if (meta && typeof meta.is_intro_offer === 'boolean') return meta.is_intro_offer;
      if (meta) {
        const first = parseCents(meta.first_price);
        const recurring = parseCents(meta.recurring_price);
        if (first != null && recurring != null) return first < recurring;
      }
      if (allocation) {
        const adjustments = allocation.price_adjustments || [];
        if (adjustments.length >= 2) {
          const first = parseCents(adjustments[0].price);
          const recurring = parseCents(adjustments[1].price);
          if (first != null && recurring != null) return first < recurring;
        }
        const first = parseCents(allocation.price);
        const recurring = parseCents(allocation.per_delivery_price) || first;
        if (first != null && recurring != null) return first < recurring;
      }
      return false;
    }

    getPricesForPlan(variant, planId) {
      const meta = this.getPlanMeta(this.variantId, planId);
      const compareFallback = parseCents(variant && variant.price);
      if (meta) {
        return {
          first: parseCents(meta.first_price),
          recurring: parseCents(meta.recurring_price),
          compare: parseCents(meta.compare_at_price) || compareFallback,
        };
      }
      if (!variant || !variant.selling_plan_allocations) {
        return { first: null, recurring: null, compare: null };
      }
      const allocation = variant.selling_plan_allocations.find((a) => planIdFromAllocation(a) === String(planId));
      if (!allocation) return { first: null, recurring: null, compare: null };
      const adjustments = allocation.price_adjustments || [];
      if (adjustments.length >= 2) {
        return {
          first: parseCents(adjustments[0].price),
          recurring: parseCents(adjustments[1].price),
          compare: parseCents(allocation.compare_at_price) || compareFallback,
        };
      }
      const first = parseCents(allocation.price);
      return {
        first,
        recurring: parseCents(allocation.per_delivery_price) || first,
        compare: parseCents(allocation.compare_at_price) || compareFallback,
      };
    }

    getVariant(variantId) {
      return this.variants.find((v) => String(v.id) === String(variantId));
    }

    getPlansForVariant(variantId) {
      return this.plansByVariant[String(variantId)] || [];
    }

    variantSupportsSubscription(variant) {
      if (this.getPlansForVariant(this.variantId).length > 0) return true;
      return !!(variant && (variant.selling_plan_allocations || []).length);
    }

    mergeVariantFromDetail(detail) {
      const html = detail && detail.html;
      if (!html || typeof html.querySelector !== 'function') return;
      const script = html.querySelector('script[type="application/json"][data-selected-variant]');
      if (!script) return;
      try {
        const incoming = JSON.parse(script.textContent);
        if (!incoming || incoming.id == null) return;
        const idx = this.variants.findIndex((v) => String(v.id) === String(incoming.id));
        if (idx >= 0) {
          this.variants[idx] = Object.assign({}, this.variants[idx], incoming);
        } else {
          this.variants.push(incoming);
        }
      } catch (err) {
        /* ignore */
      }
    }

    resolveSubscribePlanId(variant) {
      const select = this.root.querySelector('[data-pdp-frequency-select]');
      let planId = select && select.value;
      if (planId) return planId;
      const plans = this.getPlansForVariant(this.variantId);
      if (plans.length) return String(plans[0].selling_plan_id);
      const allocations = (variant && variant.selling_plan_allocations) || [];
      if (allocations.length) return planIdFromAllocation(allocations[0]);
      return '';
    }

    bindAddToCart() {
      const form = document.getElementById(this.formId);
      const btn = document.getElementById('x-atc-button-' + this.sectionId);
      const handler = () => this.injectSellingPlanIntoForm(form);
      if (form) form.addEventListener('submit', handler, true);
      if (btn) btn.addEventListener('click', handler, true);
    }

    injectSellingPlanIntoForm(form) {
      if (!form || this.root.dataset.supported !== 'true') return;
      form.querySelectorAll('input[data-pdp-selling-plan-sync]').forEach((n) => n.remove());
      if (this.mode !== 'subscribe') return;

      const select = this.root.querySelector('[data-pdp-frequency-select]');
      const planId = select ? select.value : '';
      if (!planId) return;

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      input.value = planId;
      input.setAttribute('data-pdp-selling-plan-sync', '');
      form.appendChild(input);

      const widget = this.getSealWidgetEl();
      if (widget) {
        widget.querySelectorAll('input, select, textarea').forEach((el) => {
          if (!el.name || el.disabled) return;
          if (el.name === 'selling_plan' || el.name.indexOf('selling_plan') !== -1) {
            const clone = el.cloneNode(true);
            clone.setAttribute('data-pdp-selling-plan-sync', '');
            form.appendChild(clone);
          }
        });
      }
    }

    bindUi() {
      this.root.querySelectorAll('[data-pdp-purchase-radio]').forEach((radio) => {
        radio.addEventListener('change', () => {
          if (radio.checked) this.setMode(radio.value);
        });
      });
      this.root.querySelectorAll('[data-pdp-purchase-mode]').forEach((card) => {
        card.addEventListener('click', (e) => {
          if (
            e.target.closest(
              'select, button, a, textarea, shopify-payment-terms, label[for], [data-pdp-purchase-radio]'
            )
          ) {
            return;
          }
          this.setMode(card.dataset.pdpPurchaseMode);
        });
        if (card.getAttribute('role') === 'radio') {
          card.addEventListener('keydown', (e) => this.onPurchaseOptionKeydown(e));
        }
      });
      const freq = this.root.querySelector('[data-pdp-frequency-select]');
      if (freq) {
        freq.addEventListener('change', () => this.onFrequencyChange(freq.value));
      }
    }

    onPurchaseOptionKeydown(e) {
      const cards = Array.from(this.root.querySelectorAll('[data-pdp-purchase-mode][role="radio"]'));
      if (!cards.length) return;

      const activeCard = e.target.closest('[data-pdp-purchase-mode][role="radio"]');
      if (!activeCard) return;

      if (e.target.closest('[data-pdp-frequency-select], select, button, a, shopify-payment-terms')) return;

      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        this.setMode(activeCard.dataset.pdpPurchaseMode);
        return;
      }

      if (e.key !== 'ArrowDown' && e.key !== 'ArrowRight' && e.key !== 'ArrowUp' && e.key !== 'ArrowLeft') {
        return;
      }

      e.preventDefault();
      const currentIdx = cards.indexOf(activeCard);
      const delta = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
      const nextIdx = (currentIdx + delta + cards.length) % cards.length;
      this.setMode(cards[nextIdx].dataset.pdpPurchaseMode);
      cards[nextIdx].focus({ preventScroll: true });
    }

    syncPurchaseOptionA11y() {
      this.root.querySelectorAll('[data-pdp-purchase-mode][role="radio"]').forEach((card) => {
        const selected = card.dataset.pdpPurchaseMode === this.mode;
        card.setAttribute('aria-checked', selected ? 'true' : 'false');
        card.setAttribute('tabindex', '0');
      });
    }

    bindSealEvents() {
      document.addEventListener('sealsubs:price_update', (e) => this.onSealPriceUpdate(e));
      document.addEventListener('sealsubs:selling_plan_changed', (e) => this.onSealPlanChanged(e));
      document.addEventListener('sealsubs:subscription_widget_created', () => this.syncSealPlanFromUi());
    }

    bindVariantEvents() {
      document.addEventListener('eurus:product-page-variant-select:updated:' + this.sectionId, (e) => {
        const input = document.getElementById('update-variant-' + this.sectionId);
        if (input && input.value) {
          this.applyVariant(input.value, e.detail);
          requestAnimationFrame(() => {
            this.syncUiAfterVariantChange();
            this.scheduleQuickViewHeaderPriceRefresh();
          });
        }
      });
    }

    getSealWidgetEl() {
      return this.sealMount || this.root.querySelector('.sealsubs-target-element');
    }

    getPaymentTermsEl() {
      return document.getElementById('payment-terms-' + this.sectionId);
    }

    getPaymentTermsHome() {
      return document.querySelector('.main-product-' + this.sectionId + ' .x-block-price');
    }

    startPaymentTermsLoading() {
      const wrap = this.root.querySelector('[data-pdp-payment-terms-wrap]');
      const skeleton = wrap && wrap.querySelector('[data-pdp-payment-terms-skeleton]');
      const mount = this.root.querySelector('[data-pdp-payment-terms-mount]');
      if (!wrap || !skeleton || !mount) return;
      this._paymentTermsLoadingStartedAt = Date.now();
      this._paymentTermsLoadToken = (this._paymentTermsLoadToken || 0) + 1;
      const token = this._paymentTermsLoadToken;
      skeleton.classList.remove('hidden');
      mount.classList.add('invisible');
      this.pollPaymentTermsReady(token);
    }

    revealPaymentTerms() {
      const wrap = this.root.querySelector('[data-pdp-payment-terms-wrap]');
      const skeleton = wrap && wrap.querySelector('[data-pdp-payment-terms-skeleton]');
      const mount = this.root.querySelector('[data-pdp-payment-terms-mount]');
      this._paymentTermsLoadToken = (this._paymentTermsLoadToken || 0) + 1;
      if (skeleton) skeleton.classList.add('hidden');
      if (mount) mount.classList.remove('invisible');
    }

    stopPaymentTermsLoading() {
      const wrap = this.root.querySelector('[data-pdp-payment-terms-wrap]');
      const skeleton = wrap && wrap.querySelector('[data-pdp-payment-terms-skeleton]');
      const mount = this.root.querySelector('[data-pdp-payment-terms-mount]');
      const startedAt = this._paymentTermsLoadingStartedAt || 0;
      const elapsed = Date.now() - startedAt;
      const minVisibleMs = 260;
      const hide = () => {
        if (skeleton) skeleton.classList.add('hidden');
        if (mount) mount.classList.remove('invisible');
      };
      if (elapsed < minVisibleMs) {
        setTimeout(hide, minVisibleMs - elapsed);
      } else {
        hide();
      }
    }

    pollPaymentTermsReady(token) {
      const terms = this.getPaymentTermsEl();
      if (!terms) return;
      const tryReveal = () => {
        if (token !== this._paymentTermsLoadToken) return;
        if (paymentTermsReady(terms)) {
          this.stopPaymentTermsLoading();
          return;
        }
        requestAnimationFrame(tryReveal);
      };
      tryReveal();
      setTimeout(() => {
        if (token === this._paymentTermsLoadToken) {
          this.stopPaymentTermsLoading();
        }
      }, 6000);
    }

    mountPaymentTerms() {
      const mount = this.root.querySelector('[data-pdp-payment-terms-mount]');
      const terms = this.getPaymentTermsEl();
      if (!mount || !terms) return;
      if (terms.parentElement !== mount) mount.appendChild(terms);
      terms.classList.remove('hidden');
      // 切换一次性/订阅不会重载 Shop Pay 分期组件，仅移动 DOM；已渲染则直接展示
      if (paymentTermsReady(terms)) {
        this.revealPaymentTerms();
        return;
      }
      this.startPaymentTermsLoading();
    }

    restorePaymentTerms() {
      const home = this.getPaymentTermsHome();
      const terms = this.getPaymentTermsEl();
      if (!home || !terms) return;
      if (terms.parentElement !== home) home.appendChild(terms);
    }

    syncPaymentTermsFromHtml(detail) {
      const html = detail && detail.html;
      if (!html || typeof html.getElementById !== 'function') return;
      const source = html.getElementById('payment-terms-' + this.sectionId);
      const destination = this.getPaymentTermsEl();
      if (!source || !destination) return;
      destination.innerHTML = source.innerHTML;
      const installmentInput = destination.querySelector('input[name="id"]');
      if (installmentInput) installmentInput.value = this.variantId;
      this.startPaymentTermsLoading();
      if (this.mode === 'onetime') this.syncMobileSummaryInstallment(true);
    }

    readPriceCentsFromHtml(html) {
      if (!html || typeof html.getElementById !== 'function') return null;
      const priceRoot = html.getElementById('price-' + this.sectionId);
      if (!priceRoot) return null;
      const compareNode = priceRoot.querySelector('.target-compare-at-price');
      const priceNode = priceRoot.querySelector('.target-price');
      const compare = compareNode ? Number(compareNode.textContent) : null;
      const price = priceNode ? Number(priceNode.textContent) : null;
      if (!price || isNaN(price)) return null;
      return {
        price,
        compare: compare && !isNaN(compare) && compare > 0 ? compare : null,
      };
    }

    readOnetimeCents(variant, detail) {
      const saleBlock = this.root.querySelector('[data-pdp-onetime-price-sale]');
      let price = parseCents(variant && variant.price);
      let compare = parseCents(variant && variant.compare_at_price);

      const fromHtml = detail && detail.html ? this.readPriceCentsFromHtml(detail.html) : null;
      if (fromHtml) {
        if (fromHtml.price != null) price = fromHtml.price;
        if (fromHtml.compare != null && fromHtml.compare > 0) compare = fromHtml.compare;
      }

      if (saleBlock) {
        if (price == null) price = parseCents(saleBlock.dataset.priceCents);
        if (compare == null || compare <= 0) compare = parseCents(saleBlock.dataset.compareCents);
        if (price != null) saleBlock.dataset.priceCents = String(price);
        saleBlock.dataset.compareCents =
          compare != null && compare > 0 ? String(compare) : '';
      }

      return { price, compare };
    }

    updateOnetimePrice(variant, detail) {
      if (!variant) return;
      const standard = this.root.querySelector('[data-pdp-onetime-price-standard]');
      const sale = this.root.querySelector('[data-pdp-onetime-price-sale]');
      const priceValue = this.root.querySelector('[data-pdp-onetime-price-value]');
      const saleValue = this.root.querySelector('[data-pdp-onetime-sale-value]');
      const meta = this.root.querySelector('[data-pdp-onetime-meta]');

      const { price, compare } = this.readOnetimeCents(variant, detail);
      const onSale = price != null && compare != null && compare > price;

      if (standard) standard.classList.toggle('hidden', onSale);
      if (sale) sale.classList.toggle('hidden', !onSale);
      if (priceValue) priceValue.textContent = formatMoney(price, this.moneyFormat);
      if (saleValue) saleValue.textContent = formatMoney(price, this.moneyFormat);
      if (meta) {
        if (onSale) {
          const save = compare - price;
          const pct = pctOff(compare, price);
          meta.innerHTML =
            '<span data-pdp-onetime-save-text>Save ' +
            formatMoney(save, this.moneyFormat) +
            ' (-' +
            pct +
            '%)</span> • <span class="text-gray-500 line-through" data-pdp-onetime-compare-value">' +
            formatMoney(compare, this.moneyFormat) +
            '</span>';
        } else {
          meta.innerHTML = '';
        }
      }
      if (this.mode === 'onetime') this.refreshMobileSummary();
    }

    dispatchSealPlan(sellingPlanId) {
      const widget = this.getSealWidgetEl();
      if (!widget) return;
      widget.dispatchEvent(
        new CustomEvent('sealsubs:change_selected_selling_plan_id', {
          detail: { selling_plan_id: sellingPlanId || '' },
        })
      );
    }

    setMode(mode) {
      this.mode = mode === 'subscribe' ? 'subscribe' : 'onetime';
      this.root.setAttribute('data-pdp-mode', this.mode);
      const isSub = this.mode === 'subscribe';
      const legacyCardClasses = ['border-brand-purple', 'border-gray-200', 'bg-brand-purple/5', 'bg-[#F7F6FB]'];

      this.root.querySelectorAll('[data-pdp-purchase-mode]').forEach((card) => {
        legacyCardClasses.forEach((cls) => card.classList.remove(cls));
        card.style.backgroundColor = '';
      });
      this.root.querySelectorAll('[data-pdp-purchase-radio]').forEach((radio) => {
        radio.checked = radio.value === this.mode;
      });
      this.syncPurchaseOptionA11y();

      if (isSub) {
        const select = this.root.querySelector('[data-pdp-frequency-select]');
        this.dispatchSealPlan(select ? select.value : '');
      } else {
        this.dispatchSealPlan('');
      }

      this.updateMainCta();
      this.togglePaymentButtons(!isSub);
      if (!isSub && this.root.dataset.supported === 'true') {
        this.mountPaymentTerms();
      } else if (isSub) {
        this.stopPaymentTermsLoading();
      }

      this.refreshMobileSummary();
      this.scheduleQuickViewHeaderPriceRefresh();

      document.dispatchEvent(
        new CustomEvent('pdp-hicc:purchase-mode', {
          detail: { sectionId: this.sectionId, mode: this.mode },
        })
      );
    }

    onFrequencyChange(planId) {
      if (this.mode !== 'subscribe') return;
      this.dispatchSealPlan(planId);
      const meta = this.getPlanMeta(this.variantId, planId);
      if (this.isIntroOffer(meta, null)) {
        this.setSubscribePricesLoading(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.updatePricesFromSelect());
        });
        return;
      }
      this.updatePricesFromSelect();
    }

    syncSealPlanFromUi() {
      if (this.mode === 'subscribe') {
        const select = this.root.querySelector('[data-pdp-frequency-select]');
        if (select) this.dispatchSealPlan(select.value);
      } else {
        this.dispatchSealPlan('');
      }
    }

    onSealPriceUpdate(e) {
      if (!e.detail || !this.root.contains(e.detail.element)) return;
      if (this.mode !== 'subscribe') return;
      this.updatePricesFromSelect();
    }

    onSealPlanChanged(e) {
      if (!e.detail || !this.root.contains(e.detail.element)) return;
      const select = this.root.querySelector('[data-pdp-frequency-select]');
      if (select && e.detail.selling_plan_id) select.value = String(e.detail.selling_plan_id);
      this.updatePricesFromSelect();
    }

    rebuildFrequencySelect(allocations) {
      const wrap = this.root.querySelector('[data-pdp-frequency-select]');
      if (!wrap) return;
      const select = document.createElement('select');
      select.id = wrap.id;
      select.className = wrap.className;
      select.setAttribute('data-pdp-frequency-select', '');
      (allocations || []).forEach((a, i) => {
        const opt = document.createElement('option');
        const plan = a.selling_plan || {};
        const planId = planIdFromAllocation(a);
        opt.value = planId;
        const meta = this.getPlanMeta(this.variantId, planId);
        opt.textContent = (meta && meta.frequency_label) || formatPlanLabel(plan);
        if (i === 0) opt.selected = true;
        select.appendChild(opt);
      });
      wrap.replaceWith(select);
      select.addEventListener('change', () => this.onFrequencyChange(select.value));
    }

    applyVariant(variantId, detail) {
      this.variantId = String(variantId);
      this.root.dataset.variantId = this.variantId;

      this.mergeVariantFromDetail(detail);

      let variant = this.getVariant(this.variantId);
      if (!variant && detail && detail.product) {
        try {
          const html = detail.html || detail.product;
          const parsed = typeof html === 'string' ? null : html;
          if (parsed) {
            const script = parsed.querySelector && parsed.querySelector('[id^="pdp-subscription-variants-"]');
            if (script) {
              this.variants = JSON.parse(script.textContent);
              variant = this.getVariant(this.variantId);
            }
            const plansScript = parsed.querySelector && parsed.querySelector('[id^="pdp-subscription-plans-"]');
            if (plansScript) {
              this.plansByVariant = JSON.parse(plansScript.textContent) || {};
            }
          }
        } catch (err) { /* ignore */ }
      }

      const allocations = (variant && variant.selling_plan_allocations) || [];
      const plans = this.getPlansForVariant(this.variantId);
      const supported = allocations.length > 0 || plans.length > 0;
      this.root.dataset.supported = supported ? 'true' : 'false';
      this.root.classList.toggle('hidden', !supported);

      if (detail && detail.html) {
        this.syncPaymentTermsFromHtml(detail);
      }

      this.syncHeaderPriceBlockVisibility(supported);
      if (!supported) {
        this.restorePaymentTerms();
        this.refreshMobileSummary();
        this.dispatchSealPlan('');
        this.setMode('onetime');
        if (variant) this.updateOnetimePrice(variant, detail);
        return;
      }

      this.mountPaymentTerms();
      this.setSubscribePricesLoading(true);
      const pricesWrap = this.root.querySelector('[data-pdp-sub-prices-wrap]');
      if (pricesWrap) pricesWrap.setAttribute('data-pdp-sub-prices-loading', '');
      if (allocations.length > 0) {
        this.rebuildFrequencySelect(allocations);
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.updatePricesFromSelect());
      });
      setTimeout(() => this.syncSealPlanFromUi(), 300);

      if (variant) this.updateOnetimePrice(variant, detail);

      const installmentInput = document.querySelector(
        '#product-form-installment-' + this.sectionId + ' input[name="id"]'
      );
      if (installmentInput) installmentInput.value = this.variantId;

      this.syncUiAfterVariantChange();
    }

    syncUiAfterVariantChange() {
      this.updateMainCta();
      this.togglePaymentButtons(this.mode !== 'subscribe');
      this.refreshMobileSummary();
    }

    setFutureRowLoading(loading) {
      const futureRow = this.root.querySelector('[data-pdp-sub-future-row]');
      if (!futureRow) return;
      const rowSkeleton = futureRow.querySelector('[data-pdp-future-row-skeleton-slot]');
      const rowContent = futureRow.querySelector('[data-pdp-future-row-content]');
      if (loading) {
        futureRow.classList.remove('hidden');
        if (rowSkeleton) rowSkeleton.classList.remove('hidden');
        if (rowContent) rowContent.classList.add('hidden');
      } else {
        if (rowSkeleton) rowSkeleton.classList.add('hidden');
        if (rowContent) rowContent.classList.remove('hidden');
      }
    }

    setSubscribePricesLoading(loading) {
      if (!loading) return;
      setPriceSlot(this.root.querySelector('[data-pdp-sub-today-price]'), null);
      setPriceSlot(this.root.querySelector('[data-pdp-sub-future-price]'), null);
      setSaveSlot(this.root, null);
      const pctEl = this.root.querySelector('[data-pdp-sub-future-pct]');
      setPctBadge(pctEl, null);
      setFuturePctSkeleton(pctEl, true);
      this.root.querySelectorAll('[data-pdp-sub-price-label]').forEach((el) => {
        el.classList.add('invisible');
      });
      this.setFutureRowLoading(true);
    }

    getMobileSummary() {
      return document.querySelector(
        '[data-pdp-mobile-summary][data-section-id="' + this.sectionId + '"]'
      );
    }

    beginMobileSummaryPriceLoading() {
      const wrap = this.getMobileSummary()?.querySelector('[data-pdp-mobile-summary-price-wrap]');
      const skeleton = wrap?.querySelector('[data-pdp-mobile-summary-price-skeleton]');
      if (!wrap) return;
      wrap.dataset.pdpMobileSummaryPriceLoading = 'true';
      if (skeleton) skeleton.classList.remove('hidden');
    }

    revealMobileSummaryPrice() {
      const wrap = this.getMobileSummary()?.querySelector('[data-pdp-mobile-summary-price-wrap]');
      const skeleton = wrap?.querySelector('[data-pdp-mobile-summary-price-skeleton]');
      if (!wrap) return;
      wrap.dataset.pdpMobileSummaryPriceLoading = 'false';
      if (skeleton) skeleton.classList.add('hidden');
    }

    isQuickViewContext() {
      return !!this.root.closest('#quickview-product-content');
    }

    syncHeaderPriceBlockVisibility(supported) {
      if (this.isQuickViewContext()) {
        const priceBlock = this.getQuickViewPriceRoot()?.closest('.x-block-price');
        if (priceBlock) {
          priceBlock.classList.remove('hidden', 'lg:block');
          priceBlock.classList.add('block', 'md:hidden');
        }
        return;
      }

      document.querySelectorAll('.main-product-' + this.sectionId + ' .x-block-price').forEach((priceBlock) => {
        if (supported) {
          priceBlock.classList.add('hidden');
          priceBlock.classList.remove('lg:block');
        } else {
          priceBlock.classList.add('hidden');
          priceBlock.classList.add('lg:block');
        }
      });
    }

    getQuickViewPriceRoot() {
      const root = document.getElementById('price-' + this.sectionId);
      if (!root || !root.closest('#quickview-product-content')) return null;
      return root;
    }

    scheduleQuickViewHeaderPriceRefresh() {
      if (!this.getQuickViewPriceRoot()) return;
      const run = () => this.refreshQuickViewHeaderPrice();
      requestAnimationFrame(() => requestAnimationFrame(run));
      setTimeout(run, 150);
    }

    readSubscribeTodayCentsFromUi() {
      const el = this.root.querySelector('[data-pdp-sub-today-price]');
      if (!el || el.classList.contains('invisible')) return null;
      const digits = (el.textContent || '').replace(/[^\d.]/g, '');
      const amount = parseFloat(digits);
      if (!amount || isNaN(amount)) return null;
      return Math.round(amount * 100);
    }

    resolveHeaderPrices(variant) {
      if (!variant) return null;
      if (!this.variantSupportsSubscription(variant)) {
        const fallback = this.readOnetimeCents(variant, null);
        return {
          price: fallback.price,
          compare: fallback.compare,
          showSaveBadge: false,
        };
      }
      if (this.mode === 'subscribe') {
        const select = this.root.querySelector('[data-pdp-frequency-select]');
        const planId = this.resolveSubscribePlanId(variant);
        if (select && planId) select.value = planId;
        if (!planId) {
          const fallback = this.readOnetimeCents(variant, null);
          return {
            price: fallback.price,
            compare: fallback.compare,
            showSaveBadge: true,
          };
        }
        let { first, compare } = this.getPricesForPlan(variant, planId);
        if (first == null) first = this.readSubscribeTodayCentsFromUi();
        return { price: first, compare, showSaveBadge: true };
      }
      const onetime = this.readOnetimeCents(variant, null);
      return {
        price: onetime.price,
        compare: onetime.compare,
        showSaveBadge: false,
      };
    }

    setQuickViewHeaderPrice(priceCents, compareCents, emphasizeSubscribe) {
      const root = this.getQuickViewPriceRoot();
      if (!root || priceCents == null) return;

      const priceWrap = root.querySelector('.main-product-price');
      if (!priceWrap) return;

      const targetPrice = priceWrap.querySelector('.target-price');
      const targetCompare = priceWrap.querySelector('.target-compare-at-price');
      if (targetPrice) targetPrice.textContent = String(priceCents);
      if (targetCompare) {
        targetCompare.textContent =
          compareCents != null && compareCents > 0 ? String(compareCents) : '';
      }

      const onSale = compareCents != null && compareCents > priceCents;
      const formatted = formatMoney(priceCents, this.moneyFormat);
      const formattedCompare =
        onSale && compareCents != null
          ? formatMoney(compareCents, this.moneyFormat)
          : '';

      let priceSale = priceWrap.querySelector('.price-sale');
      const regularP = priceWrap.querySelector('p.price');
      const saleRow = priceSale && priceSale.parentElement;
      let priceCompare = priceWrap.querySelector('.price-compare');

      if (onSale) {
        if (!priceSale) {
          const span = regularP?.querySelector('span');
          if (span) {
            span.classList.add('price-sale', 'price-sale--match-parent-size', 'leading-[1.2]');
            priceSale = span;
          }
        }
        if (priceSale) {
          priceSale.textContent = formatted;
          priceSale.classList.toggle('text-red-600', !emphasizeSubscribe);
          priceSale.classList.toggle('text-[#6A51B7]', !!emphasizeSubscribe);
        }
        if (regularP && priceSale && !regularP.contains(priceSale)) {
          regularP.classList.add('hidden');
        }
        if (saleRow && saleRow !== regularP) saleRow.classList.remove('hidden');

        if (!priceCompare && priceSale && formattedCompare) {
          let small = priceSale.parentElement?.querySelector('small');
          if (!small) {
            small = document.createElement('small');
            small.className =
              'cap rtl:inline-block ml-1 text-[14px] leading-[1.2] rtl:mr-1 rtl:ml-0';
            const s = document.createElement('s');
            s.className =
              'price-compare rtl:leading-tight text-[14px] text-[#6B7280] font-normal';
            small.appendChild(s);
            priceSale.parentElement?.appendChild(small);
            priceCompare = s;
          } else {
            priceCompare = small.querySelector('.price-compare');
          }
        }
        if (priceCompare && formattedCompare) {
          priceCompare.textContent = formattedCompare;
          const compareWrap = priceCompare.closest('small') || priceCompare.parentElement;
          if (compareWrap) compareWrap.classList.remove('hidden');
        }
      } else {
        if (saleRow && saleRow !== regularP) saleRow.classList.add('hidden');
        if (regularP) {
          regularP.classList.remove('hidden');
          const span = regularP.querySelector('span');
          if (span) {
            span.textContent = formatted;
            span.classList.remove('price-sale', 'text-red-600', 'text-[#6A51B7]');
          }
        } else if (priceSale) {
          priceSale.textContent = formatted;
        }
        if (priceCompare) {
          priceCompare.textContent = '';
          const compareWrap = priceCompare.closest('small');
          if (compareWrap) compareWrap.classList.add('hidden');
        }
      }
    }

    refreshQuickViewHeaderPrice(prices) {
      const resolved = prices || this.resolveHeaderPrices(this.getVariant(this.variantId));
      if (!resolved) return;
      this.setQuickViewHeaderPrice(
        resolved.price,
        resolved.compare,
        resolved.showSaveBadge && this.mode === 'subscribe'
      );
    }

    setMobileSummaryPrices(priceCents, compareCents, showSaveBadge) {
      const summary = this.getMobileSummary();
      if (!summary) return;

      const priceEl = summary.querySelector('[data-pdp-mobile-summary-price]');
      const compareEl = summary.querySelector('[data-pdp-mobile-summary-compare]');
      const saveEl = summary.querySelector('[data-pdp-mobile-summary-save]');

      const onSale =
        compareCents != null && priceCents != null && compareCents > priceCents;
      if (priceEl && priceCents != null) {
        priceEl.textContent = formatMoney(priceCents, this.moneyFormat);
        priceEl.classList.toggle('text-gray-900', !onSale);
        priceEl.classList.toggle('text-[#6A51B7]', onSale);
      }
      if (compareEl) {
        if (onSale) {
          compareEl.textContent = formatMoney(compareCents, this.moneyFormat);
          compareEl.classList.remove('hidden');
        } else {
          compareEl.textContent = '';
          compareEl.classList.add('hidden');
        }
      }
      if (saveEl) {
        if (
          showSaveBadge &&
          compareCents != null &&
          priceCents != null &&
          compareCents > priceCents
        ) {
          const pct = pctOff(compareCents, priceCents);
          saveEl.textContent = 'Save ' + pct + '% today';
          saveEl.classList.remove('hidden');
        } else {
          saveEl.textContent = '';
          saveEl.classList.add('hidden');
        }
      }
    }

    setMobileSummaryBadgesMode(isSubscribe) {
      const summary = this.getMobileSummary();
      if (!summary) return;
      const subRow = summary.querySelector('[data-pdp-mobile-summary-subscribe-row]');
      const installment = summary.querySelector('[data-pdp-mobile-summary-installment]');
      if (subRow) subRow.classList.toggle('hidden', !isSubscribe);
      if (installment && isSubscribe) {
        installment.innerHTML = '';
        installment.classList.add('hidden');
      }
    }

    syncMobileSummaryInstallment(show) {
      const summary = this.getMobileSummary();
      const installment = summary && summary.querySelector('[data-pdp-mobile-summary-installment]');
      const terms = this.getPaymentTermsEl();
      if (!installment || !terms) return;
      if (!show) {
        installment.innerHTML = '';
        installment.classList.add('hidden');
        return;
      }
      installment.innerHTML = terms.innerHTML;
      installment.classList.remove('hidden');
      const input = installment.querySelector('input[name="id"]');
      if (input) input.value = this.variantId;
    }

    refreshMobileSummary() {
      const variant = this.getVariant(this.variantId);
      const prices = this.resolveHeaderPrices(variant);
      this.refreshQuickViewHeaderPrice(prices);

      const summary = this.getMobileSummary();
      if (!summary || !prices) return;

      const supported = this.variantSupportsSubscription(variant);
      summary.classList.remove('hidden');

      if (!supported) {
        this.setMobileSummaryBadgesMode(false);
        this.syncMobileSummaryInstallment(true);
        this.setMobileSummaryPrices(prices.price, prices.compare, false);
        return;
      }
      this.beginMobileSummaryPriceLoading();

      if (this.mode === 'subscribe') {
        this.setMobileSummaryBadgesMode(true);
        this.syncMobileSummaryInstallment(false);
        this.setMobileSummaryPrices(prices.price, prices.compare, true);
        this.revealMobileSummaryPrice();
        return;
      }

      this.setMobileSummaryBadgesMode(false);
      this.syncMobileSummaryInstallment(true);
      this.setMobileSummaryPrices(prices.price, prices.compare, false);
      this.revealMobileSummaryPrice();
    }

    updatePricesFromSelect() {
      const select = this.root.querySelector('[data-pdp-frequency-select]');
      const variant = this.getVariant(this.variantId);
      if (!select || !variant) return;
      const planId = select.value;
      if (!planId) return;
      const { first, recurring, compare } = this.getPricesForPlan(variant, planId);
      const meta = this.getPlanMeta(this.variantId, planId);
      const isIntroPlan = this.isIntroOffer(meta, null);

      if (this.mode === 'subscribe') {
        this.beginMobileSummaryPriceLoading();
        this.setMobileSummaryPrices(first, compare, true);
      }

      const todayEl = this.root.querySelector('[data-pdp-sub-today-price]');
      const futureEl = this.root.querySelector('[data-pdp-sub-future-price]');
      const pctEl = this.root.querySelector('[data-pdp-sub-future-pct]');
      const futureRow = this.root.querySelector('[data-pdp-sub-future-row]');
      const thisOrderLabel = this.root.querySelector('[data-pdp-sub-price-label="this-order"]');
      const futureOrdersLabel = this.root.querySelector('[data-pdp-sub-price-label="future-orders"]');

      if (first != null) setPriceSlot(todayEl, formatMoney(first, this.moneyFormat));
      if (recurring != null) setPriceSlot(futureEl, formatMoney(recurring, this.moneyFormat));

      if (compare && first != null && compare > first) {
        const pct = pctOff(compare, first);
        const saveAmt = compare - first;
        if (isIntroPlan) {
          setSaveSlot(
            this.root,
            'Save ' +
              pct +
              '% (' +
              formatMoney(saveAmt, this.moneyFormat) +
              ') today <span class="text-gray-500 font-medium">when you subscribe.</span>'
          );
        } else {
          setSaveSlot(
            this.root,
            'Save ' + pct + '% (' + formatMoney(saveAmt, this.moneyFormat) + ') when you subscribe.'
          );
        }
      } else {
        setSaveSlot(this.root, null);
      }

      if (isIntroPlan && compare && recurring != null) {
        setPctBadge(pctEl, '(-' + pctOff(compare, recurring) + '%)');
      } else {
        setPctBadge(pctEl, null);
      }

      if (isIntroPlan) {
        this.setFutureRowLoading(false);
        if (futureRow) futureRow.classList.remove('hidden');
      } else {
        this.setFutureRowLoading(false);
        if (futureRow) futureRow.classList.add('hidden');
      }
      if (thisOrderLabel) thisOrderLabel.classList.toggle('invisible', !isIntroPlan);
      if (futureOrdersLabel) futureOrdersLabel.classList.toggle('invisible', !isIntroPlan);

      const pricesWrap = this.root.querySelector('[data-pdp-sub-prices-wrap]');
      if (pricesWrap) pricesWrap.removeAttribute('data-pdp-sub-prices-loading');

      if (this.mode === 'subscribe') {
        this.setMobileSummaryBadgesMode(true);
        this.syncMobileSummaryInstallment(false);
        this.revealMobileSummaryPrice();
        this.refreshQuickViewHeaderPrice({
          price: first,
          compare,
          showSaveBadge: true,
        });
      }
    }

    updateMainCta() {
      const btn = document.getElementById('x-atc-button-' + this.sectionId);
      if (!btn) return;
      const textEl = btn.querySelector('.x-atc-text, .button-text, [data-add-to-cart-text]') || btn;
      const label = this.mode === 'subscribe' ? 'SUBSCRIBE NOW' : 'ADD TO CART';
      const span = btn.querySelector('.x-atc-text .button-text, .button-text');
      if (span) {
        const pricePart = span.textContent.includes(' - ') ? span.textContent.split(' - ').slice(1).join(' - ') : '';
        span.textContent = pricePart ? label + ' - ' + pricePart.trim() : label;
      } else {
        textEl.textContent = label;
      }
    }

    togglePaymentButtons(show) {
      const pay = document.getElementById('x-payment-button-' + this.sectionId);
      if (pay) pay.classList.toggle('hidden', !show);
    }

  }

  function init() {
    document.querySelectorAll('.pdp-subscription-hicc').forEach((root) => {
      if (root.__pdpSubscriptionHicc) return;
      root.__pdpSubscriptionHicc = new PdpSubscriptionHicc(root);
    });
  }

  function reinitQuickViewSubscription() {
    document
      .querySelectorAll('#quickview-product-content .pdp-subscription-hicc')
      .forEach((root) => {
        root.__pdpSubscriptionHicc = null;
      });
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('quickview:content:loaded', reinitQuickViewSubscription);
  window.PdpSubscriptionHiccInit = reinitQuickViewSubscription;
})();
